import CryptoJS from 'crypto-js';
import axios from 'axios';

/**
 * HMACClient - Cliente para fazer requisições seguras com assinatura HMAC-SHA256
 *
 * Implementa o padrão de autenticação HMAC para comunicação segura com o backend.
 * Usa sincronização de relógio com o servidor para evitar HMAC_TIMESTAMP_EXPIRED.
 */
class HMACClient {
  constructor(baseURL, secretKey = null) {
    this.baseURL = baseURL;
    this.secretKey = secretKey || process.env.REACT_APP_HMAC_SECRET;

    // Clock sync: offset entre relógio local e servidor (em segundos)
    this._clockOffset = 0;
    this._clockSynced = false;
    this._syncPromise = null;

    // Criar instância do axios com configurações base
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Iniciar sincronização de relógio automaticamente
    this._syncClock();
  }

  /**
   * Sincroniza o relógio local com o servidor.
   * Calcula o offset entre o tempo local e o tempo do servidor.
   * Se falhar, usa offset 0 (relógio local).
   */
  async _syncClock() {
    if (this._syncPromise) return this._syncPromise;

    this._syncPromise = (async () => {
      try {
        const beforeRequest = Math.floor(Date.now() / 1000);
        // Construir URL do /time corretamente, independente do formato do baseURL
        const base = this.baseURL.replace(/\/+$/, '');
        const timeURL = base.includes('/api/v2') ? `${base}/time` : `${base}/api/v2/time`;
        const response = await axios.get(timeURL, { timeout: 5000 });
        const afterRequest = Math.floor(Date.now() / 1000);

        const serverTime = response.data.timestamp;
        // Estima o tempo local no momento em que o servidor respondeu
        // (média entre antes e depois da request para compensar latência)
        const localTimeAtResponse = Math.floor((beforeRequest + afterRequest) / 2);
        this._clockOffset = serverTime - localTimeAtResponse;
        this._clockSynced = true;

        if (Math.abs(this._clockOffset) > 5) {
          console.warn(`[HMAC] Clock offset detected: ${this._clockOffset}s (client is ${this._clockOffset > 0 ? 'behind' : 'ahead'})`);
        }
      } catch (error) {
        console.warn('[HMAC] Clock sync failed, using local time:', error.message);
        this._clockOffset = 0;
        this._clockSynced = false;
      } finally {
        this._syncPromise = null;
      }
    })();

    return this._syncPromise;
  }

  /**
   * Gera um nonce único para a requisição
   * @returns {string} Nonce único
   */
  generateNonce() {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Gera o timestamp ajustado com o offset do servidor
   * @returns {number} Timestamp em segundos (sincronizado com o servidor)
   */
  generateTimestamp() {
    return Math.floor(Date.now() / 1000) + this._clockOffset;
  }

  /**
   * Cria a assinatura HMAC-SHA256
   * @param {string} method - Método HTTP (GET, POST, etc.)
   * @param {string} path - Caminho da URL (ex: /subscriptions)
   * @param {number} timestamp - Timestamp Unix em segundos
   * @param {string} nonce - Nonce único da requisição
   * @param {string} body - Corpo da requisição (JSON string ou vazio)
   * @returns {string} Assinatura HMAC-SHA256 em hexadecimal
   */
  createSignature(method, path, timestamp, nonce, body = '') {
    // Formato do payload: METHOD|PATH|TIMESTAMP|NONCE|BODY
    const payload = `${method.toUpperCase()}|/api/v2${path}|${timestamp}|${nonce}|${body}`;

    // Gerar HMAC-SHA256
    const signature = CryptoJS.HmacSHA256(payload, this.secretKey).toString(CryptoJS.enc.Hex);

    // Debug detalhado para desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('=== createSignature Debug ===');
      console.log('Secret Key:', this.secretKey);
      console.log('Method:', method.toUpperCase());
      console.log('Path:', `/api/v2${path}`);
      console.log('Timestamp:', timestamp, `(offset: ${this._clockOffset}s)`);
      console.log('Nonce:', nonce);
      console.log('Body (first 200):', body.substring(0, 200));
      console.log('Payload (first 300):', payload.substring(0, 300));
      console.log('Generated Signature:', signature);
      console.log('===========================');
    }

    return signature;
  }

  /**
   * Adiciona os headers de autenticação HMAC à requisição
   * @param {Object} config - Configuração da requisição axios
   * @returns {Object} Configuração com headers de autenticação
   */
  addAuthHeaders(config) {
    const method = config.method || 'GET';
    const url = new URL(config.url, this.baseURL);
    const path = url.pathname + url.search;
    const timestamp = this.generateTimestamp();
    const nonce = this.generateNonce();

    // Preparar body para assinatura
    let body = '';
    if (config.data) {
      body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
    }

    // Criar assinatura
    const signature = this.createSignature(method, path, timestamp, nonce, body);

    // Log de debug para desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('HMAC Debug:', {
        method: method.toUpperCase(),
        path,
        timestamp,
        clockOffset: this._clockOffset,
        nonce,
        body: body.substring(0, 100) + '...',
        signature,
        secretKey: this.secretKey.substring(0, 20) + '...',
      });
    }

    // Adicionar headers de autenticação
    config.headers = {
      ...config.headers,
      'X-Signature': signature,
      'X-Timestamp': timestamp.toString(),
      'X-Nonce': nonce,
    };

    return config;
  }

  /**
   * Wrapper interno que executa a request e, se receber HMAC_TIMESTAMP_EXPIRED,
   * re-sincroniza o relógio e tenta UMA vez mais.
   */
  async _requestWithRetry(requestConfig) {
    try {
      return await this.client.request(requestConfig);
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'HMAC_TIMESTAMP_EXPIRED') {
        // Re-sync usando server_time da resposta de erro
        const serverTime = error.response?.data?.server_time;
        if (serverTime) {
          const localNow = Math.floor(Date.now() / 1000);
          this._clockOffset = serverTime - localNow;
          console.warn(`[HMAC] Re-synced clock from error response. New offset: ${this._clockOffset}s`);
        } else {
          await this._syncClock();
        }

        // Extrair config original (sem headers HMAC) e re-assinar
        const { method, url, data } = requestConfig;
        // Preservar apenas headers não-HMAC (ex: Authorization)
        const cleanHeaders = { ...requestConfig.headers };
        delete cleanHeaders['X-Signature'];
        delete cleanHeaders['X-Timestamp'];
        delete cleanHeaders['X-Nonce'];

        const retryConfig = this.addAuthHeaders({ method, url, data, headers: cleanHeaders });
        return await this.client.request(retryConfig);
      }
      throw error;
    }
  }

  async get(url, config = {}) {
    const requestConfig = this.addAuthHeaders({
      method: 'GET',
      url,
      ...config,
    });
    return this._requestWithRetry(requestConfig);
  }

  async post(url, data = {}, config = {}) {
    const requestConfig = this.addAuthHeaders({
      method: 'POST',
      url,
      data,
      ...config,
    });
    return this._requestWithRetry(requestConfig);
  }

  async put(url, data = {}, config = {}) {
    const requestConfig = this.addAuthHeaders({
      method: 'PUT',
      url,
      data,
      ...config,
    });
    return this._requestWithRetry(requestConfig);
  }

  async delete(url, config = {}) {
    const requestConfig = this.addAuthHeaders({
      method: 'DELETE',
      url,
      ...config,
    });
    return this._requestWithRetry(requestConfig);
  }

  async patch(url, data = {}, config = {}) {
    const requestConfig = this.addAuthHeaders({
      method: 'PATCH',
      url,
      data,
      ...config,
    });
    return this._requestWithRetry(requestConfig);
  }

  async request(config) {
    const requestConfig = this.addAuthHeaders(config);
    return this._requestWithRetry(requestConfig);
  }
}

export default HMACClient;
